// src/components/HeroSection.js

import React, { useRef, useContext, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import './HeroSection.css';
import videoBackground from '../7cfKwwJbdvTH5qlQZF3Yfixo.mp4';
import { ScrollContext } from './ScrollContext';
import { useNavigate } from 'react-router-dom';
import Counter from './Counter';
// ... (rest of your imports)
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNodes, faDna, faPeopleRoof, faRibbon, faMicroscope, faMobileAlt, faChevronRight, faChevronDown, faPlayCircle, faBolt, faBrain, faChartLine, faShareNodes, faDroplet
} from '@fortawesome/free-solid-svg-icons';
import Health from './CardContent/SZAPortal/Health.png';
import Omics from './CardContent/SZAPortal/Omics.png';
import AI from './CardContent/SZAPortal/AI.png';
import NewGenome from './CardContent/SZAPortal/NewGenome.png';
import OncoRisk from './CardContent/SZAPortal/OncoRisk.png';
import ContactModal from './ContactModal';


const useMediaQuery = (query) => {
  // ... (useMediaQuery implementation is correct)
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);
  return matches;
};

const Word = ({ children, progress, range }) => {
  // ... (Word component is correct)
  const opacity = useTransform(progress, range, [0.1, 1]);
  return (
    <motion.span className="ULTHOME-word-wrapper" style={{ opacity }}>
      {children}
    </motion.span>
  );
};

// HELPER COMPONENT 2: AnimatedText (UPDATED)
const AnimatedText = ({ lines, className }) => {
  const containerRef = useRef(null);
  // ✅ FIX #1: Destructure the context object here as well.
  const { containerRef: scrollContainerRef } = useContext(ScrollContext);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"],
    // Pass the correctly destructured ref
    container: scrollContainerRef,
  });

  const slowSpringConfig = { stiffness: 65, damping: 25, restDelta: 0.001 };
  const smoothTextProgress = useSpring(scrollYProgress, slowSpringConfig);

  const allWords = lines.flatMap(line => line.split(' '));
  const totalWords = allWords.length;
  let wordCounter = 0;

  return (
    <h2 ref={containerRef} className={className}>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {line.split(" ").map((word, wordIndexInLine) => {
            const start = wordCounter / totalWords;
            const end = (wordCounter + 1) / totalWords;
            wordCounter++;
            return (
              <React.Fragment key={wordIndexInLine}>
                <Word progress={smoothTextProgress} range={[start, end]}>{word}</Word>
                {' '}
              </React.Fragment>
            );
          })}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </h2>
  );
};


// YOUR MAIN HERO SECTION COMPONENT (UPDATED)
const HeroSection = ({ onScrollDown }) => {
  const sectionRef = useRef(null);
  
  // ✅ FIX #2: Destructure all needed values from the context in one go.
  const { containerRef: scrollContainerRef } = useContext(ScrollContext);
  const navigate = useNavigate();

  // ... (image cycling state and useEffect are correct) ...
  const images = [Health, Omics, AI, NewGenome, OncoRisk];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [images.length]);


  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
    // ✅ FIX #3: Now this variable holds the actual ref, not the context object.
    container: scrollContainerRef,
  });

  // ... (THE REST OF YOUR COMPONENT IS PERFECTLY FINE AND NEEDS NO CHANGES) ...
  const parallaxSpringConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const smoothParallaxProgress = useSpring(scrollYProgress, parallaxSpringConfig);
  const mainContentY = useTransform(smoothParallaxProgress, [0, 1], [0, -150]);
  const secondSectionY = useTransform(smoothParallaxProgress, [0, 1], [0, -250]);
  const slideUpY = useTransform(smoothParallaxProgress, [0.1, 0.8], [0, -120]);
  const slideUpOpacity = useTransform(smoothParallaxProgress, [0, 0.15, 0.5, 0.8], [1, 0.9, 0.4, 0]);
  const titleSlideY = useTransform(smoothParallaxProgress, [0.1, 0.7], [0, -100]);
  const ctaSlideY = useTransform(smoothParallaxProgress, [0.15, 0.8], [0, -90]);
  const statsSlideY = useTransform(smoothParallaxProgress, [0.2, 0.9], [0, -80]);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const desktopFireballBottom1 = useTransform(smoothParallaxProgress, [0, 1], ["5vh", "60vh"]);
  const desktopFireballBottom2 = useTransform(smoothParallaxProgress, [0, 1], ["0vh", "24vh"]);
  const desktopFireballBottom3 = useTransform(smoothParallaxProgress, [0, 1], ["0vh", "69vh"]);
  const desktopOrangeShapeBottom = useTransform(smoothParallaxProgress, [0, 1], ["5vh", "32vh"]);
  const mobileFireballTop1 = useTransform(smoothParallaxProgress, [0, 1], ["100vh", "30vh"]);
  const mobileFireballTop2 = useTransform(smoothParallaxProgress, [0, 1], ["60vh", "-5vh"]);
  const mobileFireballTop3 = useTransform(smoothParallaxProgress, [0, 1], ["100vh", "60vh"]);
  const mobileOrangeShapeBottom = useTransform(smoothParallaxProgress, [0, 1], ["-55vh", "-20vh"]);
  const fireball1Style = isMobile ? { top: mobileFireballTop1 } : { bottom: desktopFireballBottom1 };
  const fireball2Style = isMobile ? { top: mobileFireballTop2 } : { bottom: desktopFireballBottom2 };
  const fireball3Style = isMobile ? { top: mobileFireballTop3 } : { bottom: desktopFireballBottom3 };
  const orangeShapeStyle = isMobile ? { bottom: mobileOrangeShapeBottom } : { bottom: desktopOrangeShapeBottom };
  const secondTitleLines = [ "Powerful platforms", "for the collection, analysis,", "research and delivery", "of omics data." ];
  const bubbleData = [
    { id: 'xomics', icon: faCircleNodes, title: 'XOmics Portal', subtitle: 'Infrastructure Hub', description: 'Complete project and sample management with AI-powered omics analysis. Streamlined workflows for research teams with automated pipelines.', features: ['Project Management', 'Sample Tracking', 'Report Generation', 'AI Integration', 'Cloud Processing', 'Multi-User Access'], metrics: '25+ Tools • 16,500+ Samples', delay: 0, path: '/xomics/about' },
    { id: 'genriskpro', icon: faDna, title: 'GenRiskPro', subtitle: 'Genomic Analysis', description: 'Variant processing and structured genomic report generation', delay: 200, publication: 'https://spj.science.org/doi/full/10.34133/csbj.0011' },
    { id: 'familyrisk', icon: faPeopleRoof, title: 'FamilyRisk', subtitle: 'Family Variant Analysis', description: 'Individual, family & cohort-based newborn screening analysis', delay: 400, publication: 'https://www.researchgate.net/publication/407208579_FamilyRisk_an_integrated_genomic_web_server_for_genomic_risk_assessment_and_clinical_reporting' },
    { id: 'oncorisk', icon: faRibbon, title: 'OncoRisk', subtitle: 'Oncology Platform', description: 'Comprehensive cancer genomics with AI-assisted exploration', delay: 600, publication: 'https://www.nature.com/articles/s42003-026-10005-5' },
    { id: 'varxomics', icon: faMicroscope, title: 'VarXOmics', subtitle: 'VCF Explorer', description: 'Query-driven genomic data visualization and reporting', delay: 800, publication: 'https://www.sciencedirect.com/science/article/pii/S0022283626000409' },
    { id: 'gpgs', icon: faShareNodes, title: 'G-PGS', subtitle: 'Polygenic Scores', description: 'Polygenic risk score computation and analysis', delay: 1400, publication: 'https://www.researchsquare.com/article/rs-9510590/v1' },
    { id: 'wbtrisk', icon: faChartLine, title: 'WBTRisk', subtitle: 'Transcriptomic Health', description: 'Whole-blood transcriptome analysis and personalized reporting', delay: 1000, path: '/wbtrisk' },
    { id: 'gvep', icon: faBolt, title: 'G-VEP', subtitle: 'GPU Annotation', description: '17x faster variant annotation with CUDA acceleration', delay: 1200, publication: 'https://www.biorxiv.org/content/10.64898/2026.03.17.712498' },
    { id: 'monad', icon: faBrain, title: 'MONAD', subtitle: 'Discovery Agent', description: 'AI-powered multi-omics research assistant', delay: 1600 },
    { id: 'gpolygenesis', icon: faCircleNodes, title: 'G-Polygenesis', subtitle: 'Polygenic Modeling', description: 'Polygenic architecture analysis and simulation', delay: 1500 },
    { id: 'ggwas', icon: faBolt, title: 'G-GWAS', subtitle: 'Association Studies', description: 'GPU-accelerated genome-wide association analysis', delay: 1500 },
    { id: 'mobile', icon: faMobileAlt, title: 'Mobile App', subtitle: 'Patient Portal', description: 'AI-powered health insights with wearable integration', delay: 1800, path: '/mobileapp/about', fullWidth: true },
    { id: 'proteorisk', icon: faDroplet, title: 'ProteoRisk', subtitle: 'Blood Proteomics', description: 'Disease risk scoring from blood proteins', delay: 1700, path: '/proteorisk' }
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <section className="ULTHOME-hero-section" ref={sectionRef}>
        <motion.video className="ULTHOME-hero-video" autoPlay muted loop playsInline initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}>
          <source src={videoBackground} type="video/mp4" />
        </motion.video>
        <motion.div className="ULTHOME-video-overlay" initial={{ opacity: 1 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}></motion.div>
        <div className="ULTHOME-content-section ULTHOME-section-1">
          <div className="ULTHOME-hero-grid">
            <motion.div className="ULTHOME-main-content" style={{ y: mainContentY, opacity: slideUpOpacity }} initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}>
              <motion.h1 className="ULTHOME-title" style={{ y: titleSlideY, opacity: slideUpOpacity }}>
                <div className='ULTHOME-Line'> </div>
                A gateway to<br />
                multi-omics <span className="ULTHOME-highlight">research</span><br />
                and <span className="ULTHOME-highlight">discovery</span>.
              </motion.h1>
              <motion.div className="ULTHOME-cta-section-cover" style={{ y: ctaSlideY, opacity: slideUpOpacity }}>
                <div className="ULTHOME-cta-section">
                  <div className="ULTHOME-cta-text">
                    <h3>See Platforms in action</h3>
                    <p>Join our guided tour and explore<br />all features live.</p>
                  </div>
                </div>
                <div className='ULTHOME-Line1'> </div>
                <button className="ULTHOME-demo-btn" onClick={openModal}>Get In Contact</button>
              </motion.div>
              <motion.div className="ULTHOME-stats-row" style={{ y: statsSlideY, opacity: slideUpOpacity }}>
                <div className="ULTHOME-stat">
                  <span className="ULTHOME-stat-number"><Counter from={18} to={25} duration={3} slowIncrement={7} slowDuration={3} /><span className="ULTHOME-percent"></span></span>
                  <div className="ULTHOME-stat-info"><span className="ULTHOME-stat-label">Bioinformatic Tool Integrations</span><span className="ULTHOME-stat-sublabel">In-House and Growing</span></div>
                </div>
                <div className="ULTHOME-stat">
                  <span className="ULTHOME-stat-number"><Counter from={12000} to={16502} duration={3} slowIncrement={10} slowDuration={7} /><span className="ULTHOME-percent"></span></span>
                  <div className="ULTHOME-stat-info"><span className="ULTHOME-stat-label">Samples Processed</span><span className="ULTHOME-stat-sublabel">Across Platforms</span></div>
                </div>
              </motion.div>
            </motion.div>
            <motion.div className="ULTHOME-platform-card" style={{ y: slideUpY}} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 1 }} >
              <div className="ULTHOME-platform-grid" >
                <div className="ULTHOME-platform-cards-grid" >
                  {bubbleData.map((platform) => (
                    <motion.div key={platform.id} className={`ULTHOME-platform-card-item${platform.fullWidth ? ' ULTHOME-platform-card-item--full' : ''}`} style={{ cursor: 'pointer' }} onClick={() => navigate(platform.path || `/${platform.id}`)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1.2 + platform.delay/1000 }}>
                      <div className="ULTHOME-platform-head">
                        <FontAwesomeIcon icon={platform.icon} className="ULTHOME-platform-icon" />
                        <div className="ULTHOME-platform-headtext">
                          <h3 className="ULTHOME-platform-title">{platform.title}</h3>
                          <p className="ULTHOME-platform-subtitle">{platform.subtitle}</p>
                        </div>
                      </div>
                      <p className="ULTHOME-platform-description">{platform.description}</p>
                      {platform.features && (<div className="ULTHOME-platform-features">{platform.features.map((feature, index) => (<span key={index} className="ULTHOME-platform-feature">{feature}{index < platform.features.length - 1 && <span className="ULTHOME-feature-separator">•</span>}</span>))}</div>)}
                      {platform.metrics && (<div className="ULTHOME-platform-metrics">{platform.metrics}</div>)}
                      {!platform.comingSoon && !platform.publication && <div className="ULTHOME-platform-arrow"><FontAwesomeIcon icon={faChevronRight} /></div>}
                      {platform.publication && (
                        <a
                          className="ULTHOME-platform-pub"
                          href={platform.publication}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Publication ↗
                        </a>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div className="ULTHOME-content-section ULTHOME-section-2" style={{ y: secondSectionY }}>
          <div className="ULTHOME-second-grid">
            <motion.div className="ULTHOME-fireball-bg-1" style={fireball1Style} />
            <motion.div className="ULTHOME-fireball-bg-2" style={fireball2Style} />
            <motion.div className="ULTHOME-fireball-bg-3" style={fireball3Style} />
            <div className="ULTHOME-second-content">
              <AnimatedText lines={secondTitleLines} className="ULTHOME-second-title" />
            </div>
            <motion.div className="ULTHOME-orange-shape" style={orangeShapeStyle}></motion.div>
          </div>
        </motion.div>
      </section>
      <ContactModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

export default HeroSection;